import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import Api from "../../../../shared/api/api";
import { isUnverifiedOverall } from "../components/rankingSelection";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import "../styles/AdminRankings.css";

const MARKETPLACE_MAP = {
  com: "USA",
  "co.uk": "UK",
  ca: "Canada",
  "com.au": "Australia",
  in: "India",
  de: "Germany",
};

const MARKETPLACE_ORDER = ["com", "co.uk", "ca", "com.au", "in", "de"];
const FORMAT_ORDER = ["ebook", "paperback", "hardcover"];

function countryLabel(code) {
  return MARKETPLACE_MAP[code] || code;
}
function isDragonCategory(cat = "") {
  return (cat || "").toLowerCase().includes("dragon");
}
function isBestSellersCategory(cat = "") {
  const c = (cat || "").toLowerCase().replace(/[–—\-():]/g, " ");
  return (
    c.includes("best sellers rank") ||
    c.includes("bestsellers rank") ||
    c.includes("best seller rank") ||
    c.includes("bestseller rank") ||
    c === "kindle store" ||
    c === "books"
  );
}

export default function AdminRankingDetails() {
  const location = useLocation();
  // 🧠 Extract title directly from the URL
  const safeTitle = decodeURIComponent(location.pathname.split("/").pop() || "");

  const [rows, setRows] = useState([]);
  const [bookImage, setBookImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [format, setFormat] = useState("ebook");
  const [market, setMarket] = useState("com");

  // 🧩 Load data from backend
  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await Api.getRankingsByBook(safeTitle);
        const raw = (Array.isArray(res?.rankings) ? res.rankings : []).filter(row => !isUnverifiedOverall(row.category));
        const normalized = raw.map((r) => ({
          ...r,
          book_title: r.book_title?.trim(),
          format: (r.format || "").toLowerCase(),
          marketplace: (r.marketplace || "").toLowerCase(),
          category: (r.category || "").toLowerCase(),
          rank: Number(r.rank),
          rank_diff: Number(r.rank_diff ?? 0),
          retrieved_at: r.retrieved_at ? new Date(r.retrieved_at) : null,
        }));
        // Keep only latest per format + marketplace + category
        const latestMap = new Map();
        for (const r of normalized) {
          const key = `${r.format}|${r.marketplace}|${r.category}`;
          const prev = latestMap.get(key);
          if (!prev || (r.retrieved_at && r.retrieved_at > prev.retrieved_at)) {
            latestMap.set(key, r);
          }
        }

        const latestRows = Array.from(latestMap.values());
        setRows(latestRows);
        setBookImage(latestRows[0]?.book_image || null);
      } catch (e) {
        console.error(e);
        setErr("Failed to load ranking details.");
      } finally {
        setLoading(false);
      }
    })();
  }, [safeTitle]);

  // 🧮 Active formats and marketplaces
  const activeFormats = useMemo(
    () => Array.from(new Set(rows.map((r) => r.format))),
    [rows]
  );

  const activeMarkets = useMemo(
    () =>
      Array.from(
        new Set(
          rows.filter((r) => r.format === format).map((r) => r.marketplace)
        )
      ),
    [rows, format]
  );

  // 🪄 Auto-fallback if the selected one isn’t available
  useEffect(() => {
    if (activeFormats.length && !activeFormats.includes(format)) {
      setFormat(activeFormats[0]);
    }
  }, [activeFormats, format]);

  useEffect(() => {
    if (activeMarkets.length && !activeMarkets.includes(market)) {
      setMarket(activeMarkets[0]);
    }
  }, [activeMarkets, market]);

  // 🧮 Filter for current format + market
  const filtered = useMemo(() => {
    const list = rows
      .filter((r) => r.format === format && r.marketplace === market)
      .slice();

    list.sort((a, b) => {
      const aBest = isBestSellersCategory(a.category);
      const bBest = isBestSellersCategory(b.category);
      if (aBest && !bBest) return -1;
      if (!aBest && bBest) return 1;
      return Number(a.rank) - Number(b.rank);
    });

    return list;
  }, [rows, format, market]);

  return (
    <AdminLayout>
      <div className="rankingsContainer">
        <Link to="/admin/rankings" className="backLink">
          ← Back to Rankings
        </Link>

        <header className="detailsHeader">
          {bookImage && (
            <img
              src={bookImage}
              alt={safeTitle}
              className="detailsCover"
              width={200}
              height={300}
            />
          )}
          <h2 className="detailsTitle">{safeTitle}</h2>
        </header>

        {/* Filters */}
        <div className="filterRows">
          <div className="filterRow">
            <span className="filterLabel">Format:</span>
            {FORMAT_ORDER.map((fmt) => (
              <button
                key={fmt}
                className={`filterBtn ${format === fmt ? "active" : ""}`}
                onClick={() => setFormat(fmt)}
                disabled={!activeFormats.includes(fmt)}
              >
                {fmt.charAt(0).toUpperCase() + fmt.slice(1)}
              </button>
            ))}
          </div>

          <div className="filterRow">
            <span className="filterLabel">Country:</span>
            {MARKETPLACE_ORDER.map((code) => (
              <button
                key={code}
                className={`filterBtn ${market === code ? "active" : ""}`}
                onClick={() => setMarket(code)}
                disabled={!activeMarkets.includes(code)}
              >
                {countryLabel(code)}
              </button>
            ))}
          </div>
        </div>

        {loading && <p className="loadingText">Loading details…</p>}
        {err && <p className="errorText">{err}</p>}

        {!loading && !err && (
          filtered.length === 0 ? (
            <p className="loadingText">No rows for the selected filters.</p>
          ) : (
            <div className="tableWrapper">
              <table className="adminTable">
                <thead>
                  <tr>
                    <th>Format</th>
                    <th>Country</th>
                    <th>Category</th>
                    <th>Rank</th>
                    <th>Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    const diff = Number(r.rank_diff || 0);
                    const emoji = isBestSellersCategory(r.category)
                      ? "🥇"
                      : isDragonCategory(r.category)
                      ? "🐉"
                      : "⚔️";
                    return (
                      <tr
                        className="fadeInRow"
                        style={{ animationDelay: `${i * 35}ms` }}
                        key={`${r.format}-${r.marketplace}-${r.category}`}
                      >
                        <td>{r.format}</td>
                        <td>{countryLabel(r.marketplace)}</td>
                        <td>
                          {emoji} {r.category}
                        </td>
                        <td className="rankValue">
                          #{Number(r.rank).toLocaleString()}
                        </td>
                        <td
                          className={
                            diff < 0
                              ? "rankUp"
                              : diff > 0
                              ? "rankDown"
                              : "rankSame"
                          }
                        >
                          <span className="diffCell">
                            {diff < 0 ? (
                              <ArrowUp size={16} />
                            ) : diff > 0 ? (
                              <ArrowDown size={16} />
                            ) : (
                              <Minus size={16} />
                            )}
                            <span className="diffNum">
                              {diff === 0
                                ? "—"
                                : `${diff < 0 ? "+" : "-"}${Math.abs(diff)}`}
                            </span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </AdminLayout>
  );
}
